import React, {
  useEffect,
  useState,
  useCallback,
  PropsWithChildren,
  useRef
} from 'react';
import {Box, Typography} from '@customink/pigment-react';
import {createPortal} from 'react-dom';
import {metrics} from '~/vendor/metrics';
import {useDispatch} from 'react-redux';
import {uploadFile} from '~/actions/UploadActions';
import {useNavigate} from 'react-router-dom';
import renderInlineSvg from '~/utils/renderInlineSvg';
import {uploadIcon} from '~/constants/InlineSvgs';
import {uploadAdd} from '~/actions/AnalyticsActions';
import {pushGtmEvent} from '~/utils/analytics';

function DropzonePortalWrapper({children}: PropsWithChildren) {
  const [mountNode, setMountNode] = useState<HTMLElement | null>(null);

  useEffect(() => {
    setMountNode(document.body);
  }, []);

  if (!mountNode) return null;

  return createPortal(children, mountNode);
}

export default function FullPageDropzone() {
  const [droppingActive, setDroppingActive] = useState(false);
  const dragCounter = useRef(0); // no re-renders
  const hasBeenDropped = useRef(false); // no re-renders

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounter.current++;
    hasBeenDropped.current = false;
    setDroppingActive(true);
  }, []);

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault();
    dragCounter.current--;
    if (dragCounter.current <= 0) {
      setDroppingActive(false);
      if (!hasBeenDropped.current) {
        pushGtmEvent({
          event: 'lab_upload_cancel',
          upload_type: 'draganddrop'
        });
      }
    }
  }, []);

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault();
  }, []);

  const handleDrop = useCallback((e: DragEvent) => {
    e.preventDefault();
    setDroppingActive(false);
    dragCounter.current = 0;
    hasBeenDropped.current = true;
    const files = Array.from(e.dataTransfer?.files || []);
    if (files.length > 0) {
      metrics.pageViewTag('ndx/upload/draganddrop');
      dispatch(
        uploadAdd({
          action: 'draganddrop'
        })
      );
      dispatch(
        uploadFile({
          // The assignment is to get just the first file for now. Multiple files to be added later. By teichmaa, Q2 2025 https://customink.monday.com/boards/8586901396/pulses/8750775037
          acceptedFile: files[0],
          onUploadStarted: () => navigate('/uploadProgress')
        })
      );
    }
  }, []);

  useEffect(() => {
    window.addEventListener('dragenter', handleDragEnter);
    window.addEventListener('dragleave', handleDragLeave);
    window.addEventListener('dragover', handleDragOver);
    window.addEventListener('drop', handleDrop);

    return () => {
      window.removeEventListener('dragenter', handleDragEnter);
      window.removeEventListener('dragleave', handleDragLeave);
      window.removeEventListener('dragover', handleDragOver);
      window.removeEventListener('drop', handleDrop);
    };
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDrop]);

  if (!droppingActive) return null;

  return (
    <DropzonePortalWrapper>
      <Box
        sx={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.41)',
          zIndex: 9999,
          pointerEvents: 'none',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexDirection: 'column',
          color: 'white',
          pb: '65px'
        }}>
        <span
          style={{width: 210, height: 210, margin: '-30px', fill: 'white'}}
          dangerouslySetInnerHTML={renderInlineSvg(uploadIcon)}
        />
        <Typography variant="headingXLarge">Drop a file</Typography>
        <Typography variant="heading1">anywhere to upload</Typography>
      </Box>
    </DropzonePortalWrapper>
  );
}
